import { useCallback, useEffect, useRef, useState } from "react";
import { notifications } from "@mantine/notifications";
import {
  Accordion,
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import {
  IconAlertTriangle,
  IconBook,
  IconExternalLink,
  IconFlask,
  IconHelpCircle,
  IconNotes,
  IconBulb,
  IconRefresh,
  IconScale,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import {
  addPrimaryMaterial,
  cancelResearch,
  dismissClaim,
  fetchDossier,
  fetchDossierForWorkstream,
  fetchWorkstreamEditions,
  proposeIdeasFromResearch,
  refreshDossier,
  removePrimaryMaterial,
  startResearch,
  updateSuggestion,
} from "./researchApi";
import {
  TARGET_COUNTRIES,
  TARGET_LANGUAGES,
  formatMarket,
} from "../_Shared/targetMarkets";
import { useBrandContext } from "../Brands/brandContext";

const RUNNING = ["Sweeping", "Reading", "Synthesizing"];

const statusColor = {
  Sweeping: "blue",
  Reading: "cyan",
  Synthesizing: "violet",
  Ready: "teal",
  Failed: "red",
};

const stageLabel = {
  Sweeping: "Asking each engine its own questions",
  Reading: "Fetching and reading the best sources",
  Synthesizing: "Merging what was read into one claim list",
};

export const kindColor = {
  Statistic: "orange",
  statistic: "orange",
  Opinion: "gray",
  opinion: "gray",
  Primary: "grape",
  primary: "grape",
  Fact: "blue",
  fact: "blue",
};

/**
 * The research behind a topical cluster: what was found, where it disagrees, and what
 * it could not settle.
 *
 * Polls only while a job is actually running. A dossier is a slow, paid artefact — the
 * common case is a page that already has one, and polling it forever would spend
 * requests on a document that has not changed since last month.
 */
export default function ResearchPanel({
  brandId,
  planId,
  workstreamId,
  onClusterUpdated,
  onIdeasProposed,
  initialCountry,
  initialLanguage,
}) {
  const { selectedBrand } = useBrandContext();
  const brandCountry = selectedBrand?.defaultTargetCountryCode || "US";
  const brandLanguage = selectedBrand?.defaultTargetLanguageCode || "en";
  const [country, setCountry] = useState(initialCountry || null);
  const [language, setLanguage] = useState(initialLanguage || null);
  const [editions, setEditions] = useState([]);
  const [marketResolved, setMarketResolved] = useState(false);
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState({
    web: true,
    social: true,
    academic: false,
    aiAnswers: true,
  });
  const [rejections, setRejections] = useState([]);
  const [answering, setAnswering] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const [freeformText, setFreeformText] = useState("");
  const pollRef = useRef(null);
  const wasRunning = useRef(false);
  const refreshParentRef = useRef(onClusterUpdated || onIdeasProposed);

  useEffect(() => {
    refreshParentRef.current = onClusterUpdated || onIdeasProposed;
  }, [onClusterUpdated, onIdeasProposed]);

  useEffect(() => {
    if (initialCountry) setCountry(initialCountry);
    if (initialLanguage) setLanguage(initialLanguage);
  }, [initialCountry, initialLanguage]);

  // Open an existing edition. Do not treat the live brand default as the identity of
  // research that was already done — a brand can change home market after the dossier exists.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await fetchWorkstreamEditions(planId, workstreamId);
      if (cancelled) return;
      const editionsList = Array.isArray(list) ? list : [];
      setEditions(editionsList);

      if (initialCountry || initialLanguage) {
        setCountry(initialCountry || brandCountry);
        setLanguage(initialLanguage || brandLanguage);
      } else {
        const usable = editionsList.filter(
          (e) =>
            e.claimCount > 0 ||
            e.status === "Ready" ||
            e.status === "Sweeping" ||
            e.status === "Reading" ||
            e.status === "Synthesizing"
        );
        const home = usable.find(
          (e) =>
            e.countryCode === brandCountry && e.languageCode === brandLanguage
        );
        const pick = home || usable[0] || editionsList[0];
        setCountry(pick?.countryCode || brandCountry);
        setLanguage(pick?.languageCode || brandLanguage);
      }
      setMarketResolved(true);
    })();
    return () => {
      cancelled = true;
    };
    // brandCountry/Language are a fallback for a cluster with no editions, not a live binding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, workstreamId, initialCountry, initialLanguage]);

  const load = useCallback(async () => {
    if (!country || !language) return null;
    setLoading(true);
    const [found, list] = await Promise.all([
      fetchDossierForWorkstream(planId, workstreamId, country, language),
      fetchWorkstreamEditions(planId, workstreamId),
    ]);
    setDossier(found);
    setEditions(Array.isArray(list) ? list : []);
    setLoading(false);
    return found;
  }, [planId, workstreamId, country, language]);

  useEffect(() => {
    if (!marketResolved) return;
    load();
  }, [marketResolved, load]);

  // Poll while the job runs, stop the moment it does not.
  useEffect(() => {
    const running = dossier && RUNNING.includes(dossier.summary.status);
    if (!running) {
      if (wasRunning.current) {
        wasRunning.current = false;
        refreshParentRef.current?.();
      }
      clearInterval(pollRef.current);
      return undefined;
    }

    wasRunning.current = true;
    pollRef.current = setInterval(async () => {
      const fresh = await fetchDossier(dossier.summary.id);
      setDossier(fresh);
    }, 5000);

    return () => clearInterval(pollRef.current);
  }, [dossier]);

  const run = async (action) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (e) {
      setError(e?.message || "Research could not be started.");
    } finally {
      setBusy(false);
    }
  };

  const handleStart = () =>
    run(async () => {
      await startResearch({
        brandId,
        contentPlanId: planId,
        workstreamId,
        profile,
        country,
        language,
      });
      refreshParentRef.current?.();
    });

  const handleAnswer = async (suggestionId) => {
    await run(() => addPrimaryMaterial(dossier.summary.id, answerText, suggestionId));
    setAnswering(null);
    setAnswerText("");
  };

  const handleFreeformAdd = async () => {
    if (!dossier || !freeformText.trim()) return;
    await run(() => addPrimaryMaterial(dossier.summary.id, freeformText));
    setFreeformText("");
  };

  const handleDismiss = (suggestionId) =>
    run(() => updateSuggestion(dossier.summary.id, suggestionId, "Dismissed"));

  const handleWithdraw = (contributionId) =>
    run(() => removePrimaryMaterial(dossier.summary.id, contributionId));

  const handleRefresh = () => run(() => refreshDossier(dossier.summary.id));
  const handleCancel = () => run(() => cancelResearch(dossier.summary.id));

  const handleDismissClaim = (claim) => {
    if (!dossier || dossier.claims.length <= 1) {
      modals.open({
        title: "Keep at least one claim",
        children: (
          <Text size="sm">
            A cluster with no claims cannot be written. Research again if this
            whole dossier is wrong.
          </Text>
        ),
      });
      return;
    }
    modals.openConfirmModal({
      title: "Remove this claim",
      children: (
        <Text size="sm">
          Remove “{claim.text.slice(0, 140)}
          {claim.text.length > 140 ? "…" : ""}”? Articles that cited it will no
          longer use it.
        </Text>
      ),
      labels: { confirm: "Remove", cancel: "Keep" },
      confirmProps: { color: "red" },
      onConfirm: () =>
        run(async () => {
          await dismissClaim(dossier.summary.id, claim.id);
          refreshParentRef.current?.();
        }),
    });
  };

  const handlePropose = async () => {
    const stillOpen = dossier?.suggestedPrimaryResearch?.some(
      (s) => s.status === "Open"
    );
    if (stillOpen) {
      notifications.show({
        title: "Primary research still open",
        message: "These articles will not include your numbers yet.",
        color: "yellow",
      });
    }
    setBusy(true);
    setError(null);
    try {
      const result = await proposeIdeasFromResearch(
        planId,
        workstreamId,
        undefined,
        dossier?.summary?.id
      );
      setRejections(result.rejected || []);
      await refreshParentRef.current?.();
    } catch (e) {
      setError(e?.message || "Ideas could not be proposed.");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !marketResolved || !country || !language) {
    return <Loader size="sm" />;
  }

  const marketLabel = formatMarket(country, language);
  const otherEditions = editions.filter(
    (e) =>
      !(e.countryCode === country && e.languageCode === language)
  );

  const sourceChecks = (
    <Group gap="lg">
      <Checkbox
        label="X discourse"
        description="What practitioners complain about"
        checked={profile.social}
        onChange={(e) =>
          setProfile({ ...profile, social: e.currentTarget.checked })
        }
      />
      <Checkbox
        label="arXiv"
        description="Only for topics with a literature"
        checked={profile.academic}
        onChange={(e) =>
          setProfile({ ...profile, academic: e.currentTarget.checked })
        }
      />
      <Checkbox
        label="Google's AI answer"
        description="The consensus to beat"
        checked={profile.aiAnswers}
        onChange={(e) =>
          setProfile({ ...profile, aiAnswers: e.currentTarget.checked })
        }
      />
    </Group>
  );

  const marketFields = (
    <Stack gap={6}>
      <Group grow>
        <Select
          label="Country"
          description="The market, not the language you type in"
          data={TARGET_COUNTRIES}
          value={country}
          onChange={(v) => v && setCountry(v)}
          searchable
          allowDeselect={false}
        />
        <Select
          label="Language"
          description="How people there search and read"
          data={TARGET_LANGUAGES}
          value={language}
          onChange={(v) => v && setLanguage(v)}
          searchable
          allowDeselect={false}
        />
      </Group>
      <Text size="xs" c="dimmed">
        {`You can brief in English. Research still runs as ${marketLabel}: local law, process, standards and names. A different country spends one of this month's researched clusters. Translating afterwards keeps these claims.`}
      </Text>
    </Stack>
  );

  const advanced = (
    <Accordion variant="default">
      <Accordion.Item value="advanced">
        <Accordion.Control>
          <Text size="sm" c="dimmed">
            Advanced
          </Text>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap="sm">
            {marketFields}
            {sourceChecks}
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );

  const otherEditionBadges =
    otherEditions.length > 0 ? (
      <Group gap="xs">
        <Text size="xs" c="dimmed">
          Other markets:
        </Text>
        {otherEditions.map((e) => (
          <Badge
            key={e.id}
            variant="light"
            style={{ cursor: "pointer" }}
            onClick={() => {
              if (e.countryCode) setCountry(e.countryCode);
              if (e.languageCode) setLanguage(e.languageCode);
            }}
          >
            {formatMarket(e.countryCode, e.languageCode)}
          </Badge>
        ))}
      </Group>
    ) : null;

  if (!dossier) {
    return (
      <Stack gap="sm">
        {otherEditionBadges}
        {error && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {error}
          </Alert>
        )}
        <Button
          leftSection={<IconFlask size={16} />}
          onClick={handleStart}
          loading={busy}
        >
          Research this cluster for {marketLabel}
        </Button>
        {advanced}
      </Stack>
    );
  }

  const { summary } = dossier;
  const running = RUNNING.includes(summary.status);
  const openPrimaryCount = dossier.suggestedPrimaryResearch.filter(
    (s) => s.status === "Open"
  ).length;
  const accordionDefault = openPrimaryCount > 0 ? ["primary"] : [];

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="nowrap">
        <Group gap="xs">
          <Badge color={statusColor[summary.status] || "gray"} variant="light">
            {summary.status}
          </Badge>
          {(dossier.countryCode || dossier.languageCode) && (
            <Badge color="grape" variant="light">
              {formatMarket(dossier.countryCode, dossier.languageCode)}
            </Badge>
          )}
          {running && <Loader size="xs" />}
          {summary.isStale && summary.status === "Ready" && (
            <Tooltip label="Researched more than 30 days ago. A topic moves; a refresh re-reads it.">
              <Badge color="yellow" variant="light">
                Stale
              </Badge>
            </Tooltip>
          )}
        </Group>
        <Group gap="xs">
          {running ? (
            <Button
              size="xs"
              variant="subtle"
              color="red"
              leftSection={<IconX size={14} />}
              onClick={handleCancel}
              loading={busy}
            >
              Cancel
            </Button>
          ) : (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconRefresh size={14} />}
              onClick={handleRefresh}
              loading={busy}
            >
              Research again
            </Button>
          )}
        </Group>
      </Group>

      {otherEditionBadges}
      {advanced}

      {running && (
        <Text size="sm" c="dimmed">
          {stageLabel[summary.status]}…
        </Text>
      )}

      {summary.status === "Ready" && (
        <Group gap="xs">
          <Button
            size="xs"
            variant="light"
            color="grape"
            leftSection={<IconBulb size={14} />}
            onClick={handlePropose}
            loading={busy}
          >
            Propose articles from this research
          </Button>
          <Text size="xs" c="dimmed">
            Keeps anything you have already approved, briefed or written.
          </Text>
        </Group>
      )}

      {/* Rejections are shown rather than swallowed: a cluster that keeps producing
          single-source ideas is telling you its research is thin. */}
      {rejections.length > 0 && (
        <Alert
          color="yellow"
          variant="light"
          icon={<IconAlertTriangle size={16} />}
          title={`${rejections.length} proposed article${
            rejections.length === 1 ? " was" : "s were"
          } rejected`}
        >
          <Stack gap={4}>
            {rejections.map((r, i) => (
              <Text size="xs" key={i}>
                <b>{r.title}</b> — {r.reason}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      {summary.status === "Failed" && summary.errorMessage && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Research failed">
          {summary.errorMessage}
        </Alert>
      )}

      <Group gap="xl">
        <Stat label="Claims" value={summary.claimCount} />
        <Stat label="Sources" value={summary.sourceCount} />
      </Group>

      <Accordion variant="separated" multiple defaultValue={accordionDefault}>
        {dossier.tensions.length > 0 && (
          <Accordion.Item value="tensions">
            <Accordion.Control icon={<IconScale size={16} />}>
              Where the sources disagree ({dossier.tensions.length})
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                <Text size="xs" c="dimmed">
                  Usually the only original angle available — every other article
                  reports whichever side its one source took.
                </Text>
                {dossier.tensions.map((t, i) => (
                  <Text size="sm" key={i}>
                    • {t}
                  </Text>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        <Accordion.Item value="primary">
          <Accordion.Control icon={<IconFlask size={16} />}>
            Research only you can do ({openPrimaryCount} open)
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Text size="xs" c="dimmed">
                Everyone writing about this topic has grounded search. Nobody else has
                your numbers. What you add here outranks every source below it.
              </Text>
              {dossier.suggestedPrimaryResearch.map((s) => (
                <Paper
                  key={s.id}
                  p="sm"
                  withBorder
                  opacity={s.status === "Open" ? 1 : 0.6}
                >
                  <Group justify="space-between" mb={4} wrap="nowrap">
                    <Text size="sm" fw={500}>
                      {s.description}
                    </Text>
                    <Group gap={4} wrap="nowrap">
                      <Badge size="xs" variant="light">
                        {s.effort}
                      </Badge>
                      {s.status !== "Open" && (
                        <Badge
                          size="xs"
                          variant="light"
                          color={s.status === "Answered" ? "teal" : "gray"}
                        >
                          {s.status}
                        </Badge>
                      )}
                    </Group>
                  </Group>
                  <Text size="xs" c="dimmed" mb="xs">
                    {s.whyItWouldMatter}
                  </Text>

                  {s.status === "Open" && answering !== s.id && (
                    <Group gap="xs">
                      <Button
                        size="compact-xs"
                        variant="light"
                        onClick={() => {
                          setAnswering(s.id);
                          setAnswerText("");
                        }}
                      >
                        I have this
                      </Button>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="gray"
                        onClick={() => handleDismiss(s.id)}
                      >
                        Not going to
                      </Button>
                    </Group>
                  )}

                  {answering === s.id && (
                    <Stack gap="xs">
                      <Textarea
                        autosize
                        minRows={3}
                        placeholder="What you measured, on what, and when. Keep the figures exactly as you have them."
                        value={answerText}
                        onChange={(e) => setAnswerText(e.currentTarget.value)}
                      />
                      <Group gap="xs">
                        <Button
                          size="compact-xs"
                          onClick={() => handleAnswer(s.id)}
                          loading={busy}
                          disabled={!answerText.trim()}
                        >
                          Add to the research
                        </Button>
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          onClick={() => setAnswering(null)}
                        >
                          Cancel
                        </Button>
                      </Group>
                    </Stack>
                  )}
                </Paper>
              ))}
              <Paper p="sm" withBorder>
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    Add what only you know
                  </Text>
                  <Text size="xs" c="dimmed">
                    A measurement, a customer quote, a number from your own data.
                    No matching suggestion required.
                  </Text>
                  <Textarea
                    autosize
                    minRows={3}
                    placeholder="What you measured, on what, and when. Keep the figures exactly as you have them."
                    value={freeformText}
                    onChange={(e) => setFreeformText(e.currentTarget.value)}
                  />
                  <Button
                    size="compact-xs"
                    onClick={handleFreeformAdd}
                    loading={busy}
                    disabled={!freeformText.trim()}
                  >
                    Add to the research
                  </Button>
                </Stack>
              </Paper>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        {dossier.userMaterial.length > 0 && (
          <Accordion.Item value="material">
            <Accordion.Control icon={<IconNotes size={16} />}>
              Your own material ({dossier.userMaterial.length})
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="sm">
                {dossier.userMaterial.map((m) => (
                  <Paper key={m.id} p="sm" withBorder>
                    <Group justify="space-between" mb={4} wrap="nowrap">
                      <Text size="xs" c="dimmed">
                        {new Date(m.createdUtc).toLocaleDateString()} ·{" "}
                        {m.claimIds.length} claim
                        {m.claimIds.length === 1 ? "" : "s"}
                      </Text>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="red"
                        onClick={() => handleWithdraw(m.id)}
                      >
                        Withdraw
                      </Button>
                    </Group>
                    <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                      {m.text}
                    </Text>
                  </Paper>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {dossier.claims.length > 0 && (
          <Accordion.Item value="claims">
            <Accordion.Control icon={<IconBook size={16} />}>
              Claims ({dossier.claims.length})
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                {dossier.claims.map((c) => (
                  <Paper key={c.id} p="xs" withBorder>
                    <Group gap="xs" mb={4} wrap="nowrap" justify="space-between">
                      <Group gap="xs" wrap="nowrap">
                        <Badge size="xs" color={kindColor[c.kind] || "blue"} variant="light">
                          {c.kind}
                        </Badge>
                        {c.isMultiSource && (
                          <Badge size="xs" color="teal" variant="light">
                            Multi-source
                          </Badge>
                        )}
                        {c.publishedAt && (
                          <Text size="xs" c="dimmed">
                            {new Date(c.publishedAt).toLocaleDateString()}
                          </Text>
                        )}
                      </Group>
                      <Tooltip label="Remove this claim">
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="red"
                          onClick={() => handleDismissClaim(c)}
                        >
                          <IconTrash size={12} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                    <Text size="sm">{c.text}</Text>
                    <Group gap={6} mt={4}>
                      {c.sourceUrls.map((url) => (
                        <Anchor
                          key={url}
                          href={url}
                          target="_blank"
                          rel="noreferrer noopener"
                          size="xs"
                        >
                          {hostOf(url)}
                          <IconExternalLink
                            size={10}
                            style={{ marginLeft: 3, verticalAlign: "middle" }}
                          />
                        </Anchor>
                      ))}
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {dossier.openQuestions.length > 0 && (
          <Accordion.Item value="questions">
            <Accordion.Control icon={<IconHelpCircle size={16} />}>
              What the research could not settle ({dossier.openQuestions.length})
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                {dossier.openQuestions.map((q, i) => (
                  <Text size="sm" key={i}>
                    • {q}
                  </Text>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {dossier.sources.length > 0 && (
          <Accordion.Item value="sources">
            <Accordion.Control icon={<IconExternalLink size={16} />}>
              Sources ({dossier.sources.length})
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                {dossier.sources.map((s) => (
                  <Group key={s.url} justify="space-between" wrap="nowrap" gap="xs">
                    <Stack gap={0} style={{ minWidth: 0 }}>
                      <Anchor
                        href={s.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        size="sm"
                        truncate
                      >
                        {s.title || s.url}
                      </Anchor>
                      <Text size="xs" c="dimmed">
                        {s.domain} · found by {s.engines.join(", ")}
                        {s.readError ? ` · not read: ${s.readError}` : ""}
                      </Text>
                    </Stack>
                    <Badge
                      size="xs"
                      variant="light"
                      color={s.claimCount > 0 ? "teal" : "gray"}
                    >
                      {s.claimCount > 0 ? `${s.claimCount} claims` : "not read"}
                    </Badge>
                  </Group>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {dossier.sweepQueries.length > 0 && (
          <Accordion.Item value="queries">
            <Accordion.Control>
              What each engine was asked ({dossier.sweepQueries.length})
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                {dossier.sweepQueries.map((q, i) => (
                  <Group key={i} gap="xs" align="flex-start" wrap="nowrap">
                    <Badge size="xs" variant="outline">
                      {q.engine}
                    </Badge>
                    <Text size="sm">{q.question}</Text>
                  </Group>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}
      </Accordion>
    </Stack>
  );
}

function Stat({ label, value }) {
  return (
    <Stack gap={0}>
      <Text size="lg" fw={600}>
        {value}
      </Text>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Stack>
  );
}

export function hostOf(url) {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return url;
  }
}
